import { Accordion, AccordionDetails, AccordionSummary, Stack, Typography } from '@mui/material';
import { GetExportedIntegrationsResponse } from '@vy/api-definition';
import { VyIcon } from '@vy/ui-design-system';
import { INTEGRATION_TYPES, SYNC_JOB_STATUSES } from '@vy/integrations-types';
import type { EmailMessage, SystemEvent } from '@vy/types';
import { SYSTEM_EVENT_TYPES } from '@vy/types';
import { isEmptyArray, isNotEmptyArray, ItemOf, newUUID, raiseUnreachable } from '@vy/utils';
import { sortBy } from 'lodash';
import React from 'react';
import { useToggle } from '../../../../../hooks/use-toggle';
import { getSystemEventLabel } from '../../../../../utils/get-system-event-label';
import { LightInvoiceActivityEvent } from '../LightInvoiceActivityEvent';
import type { ActivityEvent } from './activity-event-type';
import { getEmailActivities } from './get-email-activities';
import { LightInvoiceViewPageActivityEmptyState } from './LightInvoiceViewPageActivityEmptyState';

type LightInvoiceViewPageActivityProps = {
  systemEvents: SystemEvent[];
  emailMessages: EmailMessage[];
  exportedInvoiceIntegrations: GetExportedIntegrationsResponse;
};

export const LightInvoiceViewPageActivity: React.FC<LightInvoiceViewPageActivityProps> = ({
  systemEvents,
  emailMessages,
  exportedInvoiceIntegrations,
}) => {
  const [expanded, toggleExpanded] = useToggle(true);
  const activityEvents = sortActivities(systemEvents, emailMessages, exportedInvoiceIntegrations);

  return (
    <Accordion expanded={expanded} onChange={() => toggleExpanded()} sx={{ p: 0 }}>
      <AccordionSummary
        expandIcon={
          <VyIcon
            icon="ArrowDown"
            style={{
              color: '#323232',
            }}
          />
        }
        sx={{ p: 0, height: '34px', alignItems: 'center' }}
      >
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            gap: 1
          }}>
          <VyIcon icon="CalendarChartAnalytics" />
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 600,
              lineHeight: "24px"
            }}>
            Invoice history
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        <Stack
          sx={{
            gap: "16px",
            mb: 1.5,
            mt: 1.5
          }}>
          {isEmptyArray(activityEvents) ? (
            <LightInvoiceViewPageActivityEmptyState />
          ) : (
            <>
              {activityEvents.map((event, index) => (
                <LightInvoiceActivityEvent
                  label={event.label}
                  key={event.id}
                  date={event.date}
                  email={event.email}
                  success={event.success}
                  isLast={index === activityEvents.length - 1}
                />
              ))}
            </>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

function sortActivities(
  systemEvents: SystemEvent[],
  emailMessages: EmailMessage[],
  exportedInvoiceIntegrations: GetExportedIntegrationsResponse,
): ActivityEvent[] {
  const emailActivityEvents = getEmailActivities(emailMessages);
  const systemActivityEvents = formatSystemActivityEvents(systemEvents);
  const exportedInvoiceActivityEvents = getExportedInvoiceActivityEvents(exportedInvoiceIntegrations);
  const activityEvents = [...emailActivityEvents, ...systemActivityEvents, ...exportedInvoiceActivityEvents];

  return sortBy(activityEvents, (activityEvent) => new Date(activityEvent.date).getTime());
}

function getExportedInvoiceActivityEvents(
  exportedIntegrations: GetExportedIntegrationsResponse,
): ActivityEvent[] {
  return exportedIntegrations.map((exportedIntegration) =>
    getExportedInvoiceActivityEvent(exportedIntegration),
  );
}

function getExportedInvoiceActivityEvent(
  exportedIntegration: ItemOf<GetExportedIntegrationsResponse>,
): ActivityEvent {
  return {
    id: newUUID(),
    label: getExportedIntegrationLabel(exportedIntegration),
    date: exportedIntegration.createdAt,
    success: exportedIntegration.status === SYNC_JOB_STATUSES.COMPLETED,
  };
}

function getExportedIntegrationLabel(exportedIntegration: ItemOf<GetExportedIntegrationsResponse>) {
  const status =
    exportedIntegration.status === SYNC_JOB_STATUSES.COMPLETED ? 'Synced via' : 'Failed to sync to';

  switch (exportedIntegration.integration) {
    case INTEGRATION_TYPES.QUICKBOOKS:
    case INTEGRATION_TYPES.NETSUITE:
    case INTEGRATION_TYPES.SALESFORCE:
    case INTEGRATION_TYPES.HUBSPOT:
    case INTEGRATION_TYPES.S3:
    case INTEGRATION_TYPES.SLACK:
    case INTEGRATION_TYPES.VAYU:
    case INTEGRATION_TYPES.STRIPE:
    case INTEGRATION_TYPES.MORNING:
    case INTEGRATION_TYPES.ANROK:
    case INTEGRATION_TYPES.SNOWFLAKE:
    case INTEGRATION_TYPES.CONNACT:
    case INTEGRATION_TYPES.XERO:
    case INTEGRATION_TYPES.PADDLE:
      return `${status} ${exportedIntegration.integration}`;
    default:
      return raiseUnreachable(exportedIntegration.integration);
  }
}

function formatSystemActivityEvents(systemEvents: SystemEvent[]): ActivityEvent[] {
  const eventsToShow = filterEventsToShow(systemEvents);

  return eventsToShow.map((systemEvent) => ({
    id: systemEvent.id,
    label: getSystemEventLabel(systemEvent),
    date: systemEvent.timestamp ?? systemEvent.createdAt,
    success: systemEvent.type !== SYSTEM_EVENT_TYPES.INVOICE_SYNC_FAILED,
  }));
}

function filterEventsToShow(systemEvents: SystemEvent[]) {
  const eventsToShow = [
    SYSTEM_EVENT_TYPES.INVOICE_UPDATED,
    SYSTEM_EVENT_TYPES.INVOICE_APPROVED,
    SYSTEM_EVENT_TYPES.INVOICE_SYNCED,
    SYSTEM_EVENT_TYPES.INVOICE_SYNC_FAILED,
    SYSTEM_EVENT_TYPES.INVOICE_PAID,
    SYSTEM_EVENT_TYPES.INVOICE_CANCELLED,
    SYSTEM_EVENT_TYPES.INVOICE_EMAIL_OPENED,
  ] as string[];

  return systemEvents.filter((systemEvent) => {
    if (!eventsToShow.includes(systemEvent.type)) {
      return false;
    }

    if (systemEvent.type === SYSTEM_EVENT_TYPES.INVOICE_UPDATED) {
      return isNotEmptyArray(systemEvent.metadata?.editedBy);
    }

    return true;
  });
}
